/**
 * Power Credits Purchase Modal
 * Allows users to buy power credit packages via Stripe
 */

import React, { useState } from 'react';
import { X, Zap, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { API_URL, SUBSCRIPTION_PRICES } from '../config';

const CREDIT_PACKAGES = [
  {
    id: 'small',
    credits: 5,
    price: SUBSCRIPTION_PRICES.power_credits_small,
    label: 'Starter Pack',
    description: '5 power generations',
    popular: false
  },
  {
    id: 'medium',
    credits: 15,
    price: SUBSCRIPTION_PRICES.power_credits_medium,
    label: 'Popular Pack',
    description: '15 power generations',
    popular: true,
    savings: 'Save 11%'
  },
  {
    id: 'large',
    credits: 40,
    price: SUBSCRIPTION_PRICES.power_credits_large,
    label: 'Pro Pack',
    description: '40 power generations',
    popular: false,
    savings: 'Save 25%'
  }
];

export function CreditPurchaseModal({ isOpen, onClose, onSuccess, currentCredits }) {
  const [selectedPackage, setSelectedPackage] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  if (!isOpen) return null;

  const handlePurchase = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const hostUrl = window.location.origin;

      const response = await fetch(`${API_URL}/api/payments/credits/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: selectedPackage,
          host_url: hostUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Purchase failed');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.url;
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const selectedPkg = CREDIT_PACKAGES.find(p => p.id === selectedPackage);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">Purchase Power Credits</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Current Balance: <strong>{currentCredits} credits</strong>
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Package Selection */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {CREDIT_PACKAGES.map(pkg => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`relative p-6 rounded-lg border-2 transition-all ${
                  selectedPackage === pkg.id
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="font-bold text-lg mb-2">{pkg.label}</h3>
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    ${pkg.price}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{pkg.description}</p>
                  
                  {pkg.savings && (
                    <div className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                      {pkg.savings}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      ${(pkg.price / pkg.credits).toFixed(2)} per credit
                    </p>
                  </div>
                </div>

                {selectedPackage === pkg.id && (
                  <div className="absolute top-3 right-3">
                    <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* What You Get */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">What You Get:</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Each power credit = 1 multi-revision generation (5 AI versions)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Works with any subscription tier (Free, Pro, Premium)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Credits never expire</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Get 5 different versions of AI text to choose from</span>
              </li>
            </ul>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Purchase Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">Selected Package:</span>
              <span className="font-semibold">{selectedPkg?.label}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">Credits:</span>
              <span className="font-semibold">{selectedPkg?.credits} power generations</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-2xl font-bold text-purple-600">
                ${selectedPkg?.price}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={loading}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Purchase with Stripe
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500 mt-4">
            Secure payment processing by Stripe. You&rsquo;ll be redirected to complete your purchase.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
